import { FC } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Calendar, Award, Star, Briefcase, AlertCircle, User } from 'lucide-react';
import { useTherapist } from '../hooks/useTherapists';
import { Layout } from '../components/layout';
import { Button } from '../components/ui/Button';

const StarRating: FC<{ rating: number | null; totalReviews: number }> = ({ rating, totalReviews }) => {
  if (rating === null) {
    return <span className="text-sm text-gray-500 dark:text-slate-400">No reviews yet</span>;
  }
  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center" aria-label={`${rating.toFixed(1)} out of 5 stars`}>
        {[1, 2, 3, 4, 5].map((s) => (
          <Star key={s} className={`w-5 h-5 ${s <= Math.round(rating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300 dark:text-slate-600'}`} aria-hidden="true" />
        ))}
      </div>
      <span className="text-sm font-medium text-gray-700 dark:text-slate-300">{rating.toFixed(1)}</span>
      <span className="text-sm text-gray-500 dark:text-slate-400">({totalReviews} review{totalReviews !== 1 ? 's' : ''})</span>
    </div>
  );
};

/**
 * TherapistProfilePage - Displays full profile for a single therapist
 */
export const TherapistProfilePage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data: therapist, isLoading, error } = useTherapist(id || '');

  if (isLoading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
          <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8 animate-pulse">
            <div className="h-6 w-32 bg-gray-200 dark:bg-slate-700 rounded mb-8" />
            <div className="bg-white dark:bg-slate-800 rounded-2xl p-8 shadow-sm">
              <div className="flex items-center gap-6 mb-8">
                <div className="w-24 h-24 bg-gray-200 dark:bg-slate-700 rounded-full" />
                <div className="flex-1">
                  <div className="h-8 w-48 bg-gray-200 dark:bg-slate-700 rounded mb-3" />
                  <div className="h-5 w-32 bg-gray-200 dark:bg-slate-700 rounded" />
                </div>
              </div>
              <div className="space-y-3 mb-6">
                <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-full" />
                <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-4/5" />
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center px-4">
          <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-700 p-8 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" aria-hidden="true" />
            <h1 className="text-xl font-bold text-gray-900 dark:text-slate-100 mb-2">Error Loading Profile</h1>
            <p className="text-gray-600 dark:text-slate-400 mb-6">{error.message || 'An unexpected error occurred.'}</p>
            <Link to="/therapists" className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 dark:text-primary-400 font-medium">
              <ArrowLeft className="h-4 w-4" /> Back to Therapists
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  if (!therapist) {
    return (
      <Layout>
        <Helmet><title>Therapist Not Found | Massatherapy</title></Helmet>
        <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center px-4">
          <div className="max-w-md w-full backdrop-blur-lg bg-white/80 dark:bg-slate-800/80 rounded-2xl shadow-xl border border-white/20 dark:border-slate-700/50 p-8 text-center">
            <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/30">
              <User className="h-10 w-10 text-primary-600 dark:text-primary-400" aria-hidden="true" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100 mb-3">Therapist Not Found</h1>
            <p className="text-gray-600 dark:text-slate-400 mb-6">The therapist profile you are looking for does not exist or may have been removed.</p>
            <Link to="/therapists" className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-colors">
              <ArrowLeft className="h-4 w-4" /> Browse All Therapists
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const nameParts = therapist.name.split(' ');
  const initials = nameParts.length >= 2
    ? `${nameParts[0]?.[0] || ''}${nameParts[1]?.[0] || ''}`
    : therapist.name.substring(0, 2);

  return (
    <Layout>
      <Helmet>
        <title>{therapist.name} | Massatherapy</title>
        <meta name="description" content={`Book a massage therapy session with ${therapist.name}. ${therapist.bio || ''}`} />
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-slate-900 py-8 px-4">
        <div className="mx-auto max-w-3xl">
          <Link to="/therapists" className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 font-medium mb-6 transition-colors">
            <ArrowLeft className="h-4 w-4" /> Back to Therapists
          </Link>

          <div className="backdrop-blur-lg bg-white/90 dark:bg-slate-800/90 rounded-2xl shadow-lg border border-white/20 dark:border-slate-700/50 overflow-hidden">
            <div className="p-6 sm:p-8">
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 mb-6">
                <div className="flex-shrink-0">
                  {therapist.photoUrl ? (
                    <img src={therapist.photoUrl} alt={therapist.name} className="w-28 h-28 rounded-full object-cover border-4 border-white dark:border-slate-700 shadow-md" />
                  ) : (
                    <div className="w-28 h-28 rounded-full bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center border-4 border-white dark:border-slate-700 shadow-md">
                      <span className="text-3xl font-semibold text-primary-600">{initials}</span>
                    </div>
                  )}
                </div>

                <div className="text-center sm:text-left flex-1">
                  <h1 className="text-3xl font-bold text-gray-900 dark:text-slate-100 mb-2">{therapist.name}</h1>
                  <StarRating rating={therapist.rating} totalReviews={therapist.totalReviews} />
                  {therapist.yearsOfExperience > 0 && (
                    <div className="flex items-center gap-2 mt-3 justify-center sm:justify-start">
                      <Briefcase className="h-4 w-4 text-gray-400 dark:text-slate-500" aria-hidden="true" />
                      <span className="text-sm text-gray-600 dark:text-slate-400">
                        {therapist.yearsOfExperience} year{therapist.yearsOfExperience !== 1 ? 's' : ''} of experience
                      </span>
                    </div>
                  )}
                  {!therapist.isAvailable && (
                    <span className="inline-block mt-3 text-xs font-medium bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-400 px-3 py-1 rounded-full">
                      Currently unavailable
                    </span>
                  )}
                </div>
              </div>

              {therapist.bio && (
                <div className="mb-6">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-100 mb-2">About</h2>
                  <p className="text-gray-600 dark:text-slate-400 leading-relaxed">{therapist.bio}</p>
                </div>
              )}

              {therapist.qualifications.length > 0 && (
                <div className="mb-6">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-100 mb-3 flex items-center gap-2">
                    <Award className="h-5 w-5 text-primary-600 dark:text-primary-400" aria-hidden="true" />
                    Qualifications
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {therapist.qualifications.map((qual, i) => (
                      <span key={i} className="text-sm bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 px-3 py-1 rounded-full border border-green-200 dark:border-green-800">{qual}</span>
                    ))}
                  </div>
                </div>
              )}

              {therapist.specializations.length > 0 && (
                <div className="mb-8">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-100 mb-3">Specializations</h2>
                  <div className="flex flex-wrap gap-2">
                    {therapist.specializations.map((spec, i) => (
                      <span key={i} className="text-sm bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 px-3 py-1 rounded-full border border-primary-200 dark:border-primary-800">{spec}</span>
                    ))}
                  </div>
                </div>
              )}

              <Button
                variant="primary"
                size="lg"
                disabled={!therapist.isAvailable}
                onClick={() => { window.location.href = `/book?therapistId=${therapist.id}`; }}
                leftIcon={<Calendar className="h-5 w-5" />}
                className="w-full sm:w-auto"
              >
                Book with {therapist.name.split(' ')[0]}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

TherapistProfilePage.displayName = 'TherapistProfilePage';

export default TherapistProfilePage;
